/**
 * Blok "Aktualności": kategorie, wyszukiwanie, sortowanie i paginacja bez przeładowania strony.
 * Bez JS wszystko działa na zwykłych linkach i formularzu GET.
 *
 * Użycie w main.js:
 *   import { initNews } from './modules/news';
 *   initNews({ onUpdate: () => AOS.refreshHard() });
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const SEARCH_DELAY = 400;

export const initNews = (options) => {
	const root = document.querySelector('.js-news');
	if (!root) return;

	const settings = Object.assign({ onUpdate: null }, options);
	const form = root.querySelector('.js-news-form');
	const searchInput = form.querySelector('input[name="fraza"]');
	const categoryInput = form.querySelector('input[name="kategoria"]');
	const announcer = root.querySelector('.js-news-announcer');

	let controller = null;
	let searchTimer = null;
	let lastSearch = searchInput.value.trim();
	let focusSheetButton = false;

	const getResults = () => root.querySelector('.js-news-results');

	/* ------------------------------------------------------------------
	 * URL z bieżącego stanu
	 * --------------------------------------------------------------- */
	const buildUrl = () => {
		const url = new URL(form.action);
		const results = getResults();
		const category = results ? results.dataset.category : '';
		const sort = results ? results.dataset.sort : '';
		const search = searchInput.value.trim();

		if (category) url.searchParams.set('kategoria', category);
		if (search) url.searchParams.set('fraza', search);

		// Tylko jawnie wybrane "Najstarsze" – domyślne sortowanie zależy od frazy
		if (sort === 'najstarsze') url.searchParams.set('sort', sort);

		return url.toString();
	};

	/* ------------------------------------------------------------------
	 * Pobieranie i podmiana wyników
	 * --------------------------------------------------------------- */
	const setLoading = (on) => {
		const results = getResults();
		if (!results) return;
		results.setAttribute('aria-busy', on ? 'true' : 'false');
		results.classList.toggle('is-loading', on);
	};

	const load = (url, opts) => {
		const config = Object.assign({ push: true, scroll: false }, opts);

		if (controller) controller.abort();
		const current = new AbortController();
		controller = current;

		setLoading(true);

		return fetch(url, {
			headers: { 'X-News-Partial': '1' },
			credentials: 'same-origin',
			signal: current.signal,
		})
			.then((res) => {
				if (!res.ok) throw new Error(String(res.status));
				return res.text();
			})
			.then((html) => {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const fresh = doc.querySelector('.js-news-results');
				const old = getResults();

				if (!fresh || !old) {
					window.location.href = url;
					return;
				}

				old.replaceWith(fresh);
				if (config.push) window.history.pushState({ news: true }, '', url);

				if (categoryInput) categoryInput.value = fresh.dataset.category || '';
				if (announcer) announcer.textContent = fresh.dataset.countLabel || '';
				if (typeof settings.onUpdate === 'function') settings.onUpdate(fresh);

				// Po wyborze kategorii z panelu fokus wraca na przycisk (nowy po podmianie)
				if (focusSheetButton) {
					focusSheetButton = false;
					const button = fresh.querySelector('.js-news-sheet-open');
					if (button) button.focus({ preventScroll: true });
				}

				if (config.scroll) {
					fresh.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth', block: 'start' });
				}
			})
			.catch((err) => {
				if (err.name === 'AbortError') return;
				window.location.href = url;
			})
			.finally(() => {
				if (controller === current) setLoading(false);
			});
	};

	const syncSearchFromUrl = (href) => {
		const value = new URL(href, window.location.origin).searchParams.get('fraza') || '';
		searchInput.value = value;
		lastSearch = value;
	};

	/* ------------------------------------------------------------------
	 * Wyszukiwanie (z opóźnieniem podczas pisania)
	 * --------------------------------------------------------------- */
	const runSearch = () => {
		clearTimeout(searchTimer);
		const value = searchInput.value.trim();
		if (value === lastSearch) return;
		if (value.length === 1) return; // czekamy na co najmniej 2 znaki
		lastSearch = value;
		load(buildUrl());
	};

	searchInput.addEventListener('input', () => {
		clearTimeout(searchTimer);
		searchTimer = setTimeout(runSearch, SEARCH_DELAY);
	});

	form.addEventListener('submit', (e) => {
		e.preventDefault();
		lastSearch = null; // wymuś wyszukanie po Enterze
		runSearch();
	});

	/* ------------------------------------------------------------------
	 * Panel kategorii od dołu (mobile) – natywny <dialog>
	 * --------------------------------------------------------------- */
	const getSheet = () => root.querySelector('.js-news-sheet');

	const closeSheet = () => {
		const sheet = getSheet();
		if (sheet && sheet.open) sheet.close();
	};

	root.addEventListener('click', (e) => {
		if (e.target.closest('.js-news-sheet-open')) {
			const sheet = getSheet();
			if (sheet && typeof sheet.showModal === 'function') sheet.showModal();
			return;
		}

		if (e.target.closest('[data-news-sheet-close]')) {
			closeSheet();
			return;
		}

		// Kliknięcie w przyciemnione tło poza panelem
		const sheet = e.target.closest('.js-news-sheet');
		if (sheet && e.target === sheet) {
			const rect = sheet.getBoundingClientRect();
			const inside =
				e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
			if (!inside) closeSheet();
		}
	});

	/* ------------------------------------------------------------------
	 * Linki: kategorie, paginacja, czyszczenie frazy
	 * --------------------------------------------------------------- */
	root.addEventListener('click', (e) => {
		const link = e.target.closest('[data-news-link], .js-news-results a.page-numbers');
		if (!link || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

		e.preventDefault();

		const more = link.closest('.js-news-more');
		if (more) more.open = false;

		if (link.closest('.js-news-sheet')) {
			focusSheetButton = true;
			closeSheet();
		}

		syncSearchFromUrl(link.href);
		load(link.href, { scroll: link.classList.contains('page-numbers') });
	});

	// Zamykanie "Więcej kategorii" kliknięciem obok lub Esc
	document.addEventListener('click', (e) => {
		root.querySelectorAll('.js-news-more[open]').forEach((details) => {
			if (!details.contains(e.target)) details.open = false;
		});
	});

	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape') return;
		root.querySelectorAll('.js-news-more[open]').forEach((details) => {
			details.open = false;
			const summary = details.querySelector('summary');
			if (summary) summary.focus();
		});
	});

	/* ------------------------------------------------------------------
	 * Wstecz / dalej
	 * --------------------------------------------------------------- */
	window.addEventListener('popstate', () => {
		syncSearchFromUrl(window.location.href);
		load(window.location.href, { push: false });
	});
};
